<template>
  <div class="app-shell">
    <!-- Sidebar. On narrow screens it becomes a slide-in drawer (see
         responsive.css), opened from the menu button in the header. -->
    <div v-if="navOpen" class="sidebar-scrim" @click="closeNav"></div>
    <aside id="app-sidebar" ref="sidebar" class="sidebar" :class="{ 'is-open': navOpen }"
      @keydown.esc="closeNav" @keydown.tab="trapNavFocus">
      <div class="sidebar-brand">
        <div class="sidebar-brand-icon">
          <img src="/logo.png" alt="" width="36" height="36">
        </div>
        <span class="sidebar-brand-text">INTELVO</span>
        <button type="button" class="sidebar-close" aria-label="Close menu" @click="closeNav">
          <X :size="20" aria-hidden="true" />
        </button>
      </div>

      <nav class="sidebar-nav" aria-label="Main">
        <router-link to="/" class="sidebar-link" :class="{ active: $route.name === 'task-dashboard' }">
          <ListTodo :size="20" aria-hidden="true" />
          Task Dashboard
        </router-link>
        <router-link to="/sectors" class="sidebar-link" :class="{ active: ['sectors', 'sector-projects', 'taskboard'].includes($route.name) }">
          <LayoutGrid :size="20" aria-hidden="true" />
          Projects
        </router-link>
        <!-- Analytics and Reporting travel together: one permission covers both,
             so a role either gets the reporting screens or it does not. -->
        <router-link v-if="canViewAnalytics" to="/analytics" class="sidebar-link" :class="{ active: ['analytics', 'project-analytics'].includes($route.name) }">
          <ChartColumn :size="20" aria-hidden="true" />
          Analytics
        </router-link>
        <router-link v-if="canViewAnalytics" to="/reporting" class="sidebar-link" :class="{ active: $route.name === 'reporting' }">
          <FileText :size="20" aria-hidden="true" />
          Reporting
        </router-link>
        <!-- Hidden for everyone else purely as a courtesy: the page and every
             endpoint behind it enforce this independently. -->
        <router-link v-if="isAdmin" to="/roles" class="sidebar-link" :class="{ active: $route.name === 'roles' }">
          <ShieldCheck :size="20" aria-hidden="true" />
          Roles
        </router-link>
      </nav>

      <div class="sidebar-footer">
        <button class="sidebar-link" @click="signOut">
          <LogOut :size="20" aria-hidden="true" />
          Sign Out
        </button>
      </div>
    </aside>

    <!-- Main Area -->
    <div class="main-content">
      <header class="top-header">
        <button ref="navToggle" type="button" class="nav-toggle" aria-label="Open menu"
          aria-controls="app-sidebar" :aria-expanded="navOpen ? 'true' : 'false'" @click="openNav">
          <Menu :size="22" aria-hidden="true" />
        </button>
        <ThemeToggle />
        <div class="header-user">
          <!-- Name first; the email underneath, small. An account with no name
               shows its email on the first line instead, once. -->
          <div class="header-user-text">
            <span class="header-user-name">{{ currentUser?.name || currentUser?.email || 'Loading...' }}</span>
            <span v-if="currentUser?.name && currentUser?.email" class="header-user-email">{{ currentUser.email }}</span>
          </div>
          <div class="header-avatar">{{ userInitials }}</div>
        </div>
      </header>

      <!-- A page that failed to load says so here, instead of looking empty. -->
      <div v-if="error || accessNotice" class="load-error" role="alert">
        <AlertTriangle :size="18" aria-hidden="true" />
        <span class="load-error-text">{{ error || accessNotice }}</span>
        <!-- Retrying cannot grant access, so a refusal has no Try Again. -->
        <button v-if="error" type="button" class="load-error-retry" @click="retryLoad">Try Again</button>
        <button type="button" class="load-error-close" aria-label="Dismiss" @click="dismissLoadError">
          <X :size="16" aria-hidden="true" />
        </button>
      </div>

      <div class="page-content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useTaskStore } from '../stores/taskStore';
import ThemeToggle from './common/ThemeToggle.vue';
import { ListTodo, LayoutGrid, ChartColumn, FileText, ShieldCheck, LogOut, Menu, X, AlertTriangle } from 'lucide-vue-next';

export default {
  name: 'MainLayout',
  components: { ThemeToggle, ListTodo, LayoutGrid, ChartColumn, FileText, ShieldCheck, LogOut, Menu, X, AlertTriangle },
  data() {
    return {
      navOpen: false
    };
  },
  computed: {
    ...mapState(useTaskStore, ['currentUser', 'userInitials', 'isAdmin', 'can', 'error', 'accessNotice']),
    canViewAnalytics() {
      return this.can('tasks.analytics.view');
    }
  },
  watch: {
    // Picking a page is the usual way out of the mobile menu.
    $route() {
      this.navOpen = false;
    }
  },
  methods: {
    ...mapActions(useTaskStore, ['fetchCurrentUser']),
    openNav() {
      this.navOpen = true;
      this.$nextTick(() => {
        this.$refs.sidebar?.querySelector('a, button')?.focus();
      });
    },
    // While the mobile menu is open, Tab cycles through it instead of wandering
    // onto the page hidden behind the scrim. navOpen is only ever true on
    // narrow screens, so the desktop sidebar is unaffected.
    trapNavFocus(e) {
      if (!this.navOpen) return;
      const items = Array.from(this.$refs.sidebar.querySelectorAll('a[href], button:not([disabled])'));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    // Reloading re-runs every request the page makes, whichever one failed.
    retryLoad() {
      window.location.reload();
    },
    dismissLoadError() {
      const store = useTaskStore();
      store.error = null;
      store.accessNotice = null;
    },
    closeNav() {
      if (!this.navOpen) return;
      this.navOpen = false;
      this.$refs.navToggle?.focus();
    },
    async signOut() {
      try {
        // Clear all data
        useTaskStore().logout();
        // Small delay to ensure state is cleared
        await new Promise(resolve => setTimeout(resolve, 100));
        // Redirect to root
        window.location.href = '/';
      } catch (error) {
        console.error('Logout error:', error);
        // Force redirect even on error
        window.location.href = '/';
      }
    }
  },
  async mounted() {
    if (!this.currentUser) {
      await this.fetchCurrentUser();
    }
  }
}
</script>
